/** DLP の画面で使う定数。ラベルと 状態 → tone の対応はここに 1 か所(docs/dlp.md §10)。 */
import type {
  DlpBrowser,
  DlpBrowserPolicyState,
  DlpScanStatus,
  DlpScanTrigger,
  DlpUploadAction,
  DlpUploadAttribution,
  DlpUploadCategory,
  DlpUploadKind,
  DlpUploadProcessDir,
  DlpUploadState,
  DlpUsbKind,
  DlpUsbMode,
} from "../../lib/api";
import type { Tone } from "../../ui/StatusTag";

export const SCAN_STATUSES: { value: DlpScanStatus; label: string }[] = [
  { value: "queued", label: "待機中" },
  { value: "sent", label: "送信済み" },
  { value: "running", label: "実行中" },
  { value: "done", label: "完了" },
  { value: "error", label: "失敗" },
  { value: "cancelled", label: "取消" },
  { value: "reviewed", label: "確認済み" },
];

/** まだ結果が返っていない状態。手動スキャンの 409 と、パネルの自動更新の判定に使う。 */
export const SCAN_ACTIVE: DlpScanStatus[] = ["queued", "sent", "running"];

export function scanStatusLabel(v: string): string {
  return SCAN_STATUSES.find((s) => s.value === v)?.label ?? v;
}

/**
 * スキャンの状態 → tone。done は検出 0 なら success、1 件でもあれば warning
 * (「終わった」より「見るものがある」を目立たせる)。
 */
export function scanStatusTone(status: string, matches: number): Tone {
  switch (status) {
    case "sent":
    case "running":
      return "info";
    case "done":
      return matches > 0 ? "warning" : "success";
    case "error":
      return "danger";
    case "reviewed":
      return "success";
    default:
      return "neutral";
  }
}

export const SCAN_TRIGGERS: { value: DlpScanTrigger; label: string }[] = [
  { value: "offboard", label: "退職" },
  { value: "manual", label: "手動" },
];

export function triggerLabel(v: string): string {
  return SCAN_TRIGGERS.find((t) => t.value === v)?.label ?? v;
}

export const USB_KINDS: { value: DlpUsbKind; label: string }[] = [
  { value: "attached", label: "接続" },
  { value: "detached", label: "取り外し" },
  { value: "blocked", label: "遮断" },
];

export function usbKindLabel(v: string): string {
  return USB_KINDS.find((k) => k.value === v)?.label ?? v;
}

/** USB イベント → tone。遮断は「ポリシーが効いた」印なので warning で目立たせる。 */
export function usbKindTone(kind: string): Tone {
  if (kind === "attached") return "info";
  if (kind === "blocked") return "warning";
  return "neutral";
}

export const USB_MODES: { value: DlpUsbMode; label: string }[] = [
  { value: "allow", label: "許可" },
  { value: "readonly", label: "読み取り専用" },
  { value: "block", label: "遮断" },
];

export function usbModeLabel(v: string | null | undefined): string {
  if (!v) return "既定に従う";
  return USB_MODES.find((m) => m.value === v)?.label ?? v;
}

/** USB モード → tone。許可は素の色、制限は強さの順に warning / danger。 */
export function usbModeTone(v: string | null | undefined): Tone {
  if (v === "readonly") return "warning";
  if (v === "block") return "danger";
  return "neutral";
}

/** `summary.skipped_reasons` のキーの日本語。pdf は v0 の制約(§6)なので文言で補う。 */
export const SKIP_REASONS: Record<string, string> = {
  pdf: "PDF(v0 では読まない)",
  too_large: "サイズ超過",
  error: "開けない・権限なし",
  extension: "拡張子が対象外",
};

export function skipReasonLabel(k: string): string {
  return SKIP_REASONS[k] ?? k;
}

/** 検査対象のうち、本文をそのまま読む拡張子と zip 内 XML から読む拡張子(§6)。ポリシー編集の候補。 */
export const KNOWN_EXTENSIONS = [
  "txt", "csv", "md", "log", "json", "xml", "html", "htm", "ini",
  "docx", "xlsx", "pptx",
];

/** 相対ルートの候補(全ユーザープロファイルの `C:\Users\<name>\<root>` に展開される)。 */
export const KNOWN_ROOTS = ["Desktop", "Documents", "Downloads", "Pictures", "OneDrive"];

// ---- アップロード検知(R6c-2、docs/dlp-upload.md §10)----

export const UPLOAD_KINDS: { value: DlpUploadKind; label: string }[] = [
  { value: "detected", label: "検知" },
  { value: "unknown_bulk", label: "宛先不明の大量送信" },
];

export function uploadKindLabel(v: string): string {
  return UPLOAD_KINDS.find((k) => k.value === v)?.label ?? v;
}

/** 帰属。ambiguous は「共有 IP でどのサービス宛てか決まらない」(§4.4)。文言は「推定」で統一 */
export const UPLOAD_ATTRIBUTIONS: { value: DlpUploadAttribution; label: string }[] = [
  { value: "exact", label: "確定" },
  { value: "ambiguous", label: "推定" },
];

export function uploadAttributionLabel(v: string | null | undefined): string {
  if (!v) return "";
  return UPLOAD_ATTRIBUTIONS.find((a) => a.value === v)?.label ?? v;
}

/** 検知の tone。detected+exact と unknown_bulk は warning、detected+ambiguous は neutral */
export function uploadEventTone(kind: string, attribution: string | null | undefined): Tone {
  if (kind === "detected" && attribution === "ambiguous") return "neutral";
  return "warning";
}

export const UPLOAD_CATEGORIES: { value: DlpUploadCategory; label: string }[] = [
  { value: "storage", label: "クラウドストレージ" },
  { value: "transfer", label: "ファイル転送" },
  { value: "ai", label: "生成 AI" },
  { value: "other", label: "その他" },
];

export function uploadCategoryLabel(v: string | null | undefined): string {
  if (!v) return "";
  return UPLOAD_CATEGORIES.find((c) => c.value === v)?.label ?? v;
}

export const UPLOAD_ACTIONS: { value: DlpUploadAction; label: string }[] = [
  { value: "monitor", label: "監視" },
  { value: "block", label: "遮断(ブラウザ)" },
  { value: "allow", label: "除外(許可)" },
];

export function uploadActionLabel(v: string): string {
  return UPLOAD_ACTIONS.find((a) => a.value === v)?.label ?? v;
}

export function uploadActionTone(v: string): Tone {
  if (v === "block") return "danger";
  if (v === "allow") return "success";
  return "info";
}

export const PROCESS_DIRS: Record<DlpUploadProcessDir, string> = {
  program_files: "Program Files",
  windows: "Windows",
  user_profile: "ユーザーフォルダ",
  other: "その他",
  unknown: "不明",
};

export function processDirLabel(v: string): string {
  return PROCESS_DIRS[v as DlpUploadProcessDir] ?? v;
}

export const UPLOAD_STATES: Record<DlpUploadState, string> = {
  off: "停止",
  starting: "開始中",
  observing: "観測中",
  degraded: "取りこぼしあり",
  error: "エラー",
  unsupported: "非対応",
  invalid: "不正な値",
};

export function uploadStateLabel(v: string): string {
  return UPLOAD_STATES[v as DlpUploadState] ?? v;
}

/** 観測の状態 → tone。observing=success、degraded=warning、error=danger、off / unsupported=neutral */
export function uploadStateTone(v: string): Tone {
  switch (v) {
    case "observing":
      return "success";
    case "starting":
      return "info";
    case "degraded":
    case "invalid":
      return "warning";
    case "error":
      return "danger";
    default:
      return "neutral";
  }
}

export const BROWSERS: { value: DlpBrowser; label: string }[] = [
  { value: "chrome", label: "Chrome" },
  { value: "edge", label: "Edge" },
  { value: "firefox", label: "Firefox" },
];

export const BROWSER_POLICY_STATES: Record<DlpBrowserPolicyState, string> = {
  off: "書いていない",
  applied: "適用済み",
  external: "他の管理者が設定済み",
  conflict: "競合(書かない)",
  absent: "未導入",
  error: "エラー",
  invalid: "不正な値",
};

export function browserPolicyLabel(v: string): string {
  return BROWSER_POLICY_STATES[v as DlpBrowserPolicyState] ?? v;
}

export function browserPolicyTone(v: string): Tone {
  if (v === "applied" || v === "external") return "success";
  if (v === "conflict" || v === "invalid") return "warning";
  if (v === "error") return "danger";
  return "neutral";
}
